/* Test outbound — places a single call against a chosen subscriber so an
 * operator can verify the active outbound flow / recording / dialer config
 * end-to-end without enabling the full campaign. POSTs straight to the bot
 * (BOT_URL/dial); the bot looks the customer up by phone, runs the active
 * outbound flow, and the resulting call shows up in /outbound. */

import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";

import { API_URL, BOT_URL, apiFetch, fmtPhone } from "../lib/api";

type PickerCustomer = {
  id: number;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  status: string;
  plan: string | null;
};

function customerLabel(c: PickerCustomer): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
  return name || c.business_name || fmtPhone(c.phone);
}

type DialResult =
  | { kind: "ok"; phone: string; label: string }
  | { kind: "err"; message: string };

export function TestOutbound() {
  const [customers, setCustomers] = useState<PickerCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<PickerCustomer | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DialResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch<{ customers: PickerCustomer[] }>(
          "/api/customers",
        );
        if (!cancelled) setCustomers(data.customers);
      } catch (e) {
        if (!cancelled)
          setLoadErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function placeCall() {
    if (!selected || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await fetch(`${BOT_URL}/dial`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ to: selected.phone }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setResult({
          kind: "err",
          message:
            typeof data?.detail === "string"
              ? data.detail
              : `Bot returned ${r.status}`,
        });
        return;
      }
      setResult({
        kind: "ok",
        phone: selected.phone,
        label: customerLabel(selected),
      });
      setSelected(null);
    } catch (e) {
      setResult({
        kind: "err",
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="test-outbound test-outbound-loading">Loading subscribers…</div>;
  }
  if (loadErr) {
    return (
      <div className="test-outbound test-outbound-error">
        <div className="dial-err">{loadErr}</div>
      </div>
    );
  }

  return (
    <div id="test-outbound" className="test-outbound">
      {selected ? (
        <SelectedCard
          customer={selected}
          onChange={() => setSelected(null)}
          onPlaceCall={placeCall}
          busy={busy}
        />
      ) : (
        <SubscriberSearch customers={customers} onPick={setSelected} />
      )}

      {result?.kind === "ok" && (
        <div id="test-outbound-success" className="test-outbound-success">
          <span>
            Call placed to <strong>{result.label}</strong>{" "}
            <span className="test-outbound-success-meta">
              ({fmtPhone(result.phone)})
            </span>.
          </span>
          <Link
            id="test-outbound-success-link"
            href="/outbound"
            className="test-outbound-link"
          >
            View in Outbound →
          </Link>
        </div>
      )}
      {result?.kind === "err" && (
        <div id="test-outbound-err" className="dial-err">
          {result.message}
        </div>
      )}
      {!result && (
        <div className="test-outbound-hint muted">
          Uses the active outbound flow + recording config above. Bot at{" "}
          <code>{BOT_URL}</code>; calls written to <code>{API_URL || "/"}</code>.
        </div>
      )}
    </div>
  );
}

// ─── Selected subscriber card + Place call button ──────────────────────
function SelectedCard({
  customer,
  onChange,
  onPlaceCall,
  busy,
}: {
  customer: PickerCustomer;
  onChange: () => void;
  onPlaceCall: () => void;
  busy: boolean;
}) {
  return (
    <div id="test-outbound-selected" className="test-outbound-selected">
      <div className="test-outbound-selected-body">
        <div className="test-outbound-selected-name">{customerLabel(customer)}</div>
        <div className="test-outbound-selected-meta">
          <span>{fmtPhone(customer.phone)}</span>
          {customer.plan && <span> · {customer.plan}</span>}
          <span> · {customer.status}</span>
        </div>
      </div>
      <div className="test-outbound-selected-actions">
        <button
          id="test-outbound-change-btn"
          type="button"
          className="btn-secondary"
          onClick={onChange}
          disabled={busy}
        >
          Change
        </button>
        <button
          id="test-outbound-place-btn"
          type="button"
          className="test-outbound-place"
          onClick={onPlaceCall}
          disabled={busy}
        >
          {busy ? "Placing…" : "Place call"}
        </button>
      </div>
    </div>
  );
}

// ─── Subscriber search input + dropdown ────────────────────────────────
function SubscriberSearch({
  customers,
  onPick,
}: {
  customers: PickerCustomer[];
  onPick: (c: PickerCustomer) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers.slice(0, 8);
    return customers
      .filter(
        (c) =>
          customerLabel(c).toLowerCase().includes(q) || c.phone.includes(q),
      )
      .slice(0, 12);
  }, [query, customers]);

  return (
    <div className="test-outbound-picker">
      <input
        id="test-outbound-picker-input"
        className="test-outbound-picker-input"
        type="text"
        placeholder="Find a subscriber by name or phone"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && matches.length > 0 && (
        <ul
          id="test-outbound-picker-results"
          className="test-outbound-picker-results"
        >
          {matches.map((c) => (
            <li
              key={c.id}
              id={`test-outbound-picker-row-${c.id}`}
              className="test-outbound-picker-row"
              onMouseDown={(e) => {
                e.preventDefault();
                onPick(c);
                setQuery("");
                setOpen(false);
              }}
            >
              <span className="test-outbound-picker-row-label">
                {customerLabel(c)}
              </span>
              <span className="test-outbound-picker-row-meta">
                {fmtPhone(c.phone)}
                {c.plan && ` · ${c.plan}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && matches.length === 0 && query.trim() && (
        <ul className="test-outbound-picker-results">
          <li className="test-outbound-picker-row test-outbound-picker-row-empty">
            No matches.
          </li>
        </ul>
      )}
    </div>
  );
}
